import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth as SupabaseAuth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { AuthError } from "@supabase/supabase-js";
import { toast } from "sonner";

const Auth = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate("/chat");
      }
      setIsLoading(false);
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Auth state changed:", event, session); // Debug log
      if (event === "SIGNED_IN" && session) {
        navigate("/chat");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const getErrorMessage = (error: AuthError) => {
    switch (error.message) {
      case "Invalid login credentials":
        return "Email hoặc mật khẩu không hợp lệ. Vui lòng kiểm tra lại thông tin đăng nhập.";
      case "Email not confirmed":
        return "Vui lòng xác nhận địa chỉ email của bạn trước khi đăng nhập.";
      case "User not found":
        return "Không tìm thấy người dùng với thông tin này.";
      default:
        return "Đã xảy ra lỗi. Vui lòng thử lại sau.";
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-white font-sans">Đang tải...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center space-y-2">
          <div className="flex justify-center mb-4">
            <img 
              src="/lovable-uploads/37369634-e5ff-4200-b7aa-0ece5a8baa04.png" 
              alt="Mai Bot Logo" 
              className="w-32 h-32"
            />
          </div>
          <h1 className="text-3xl font-bold text-white font-sans">Mai Bot</h1>
          <p className="text-gray-400 font-sans">Đăng nhập để bắt đầu trò chuyện</p>
        </div>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertDescription className="font-sans">{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="bg-card p-6 rounded-lg shadow-xl">
          <SupabaseAuth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#6366f1',
                    brandAccent: '#4f46e5',
                  },
                  fonts: {
                    bodyFontFamily: `"Be Vietnam Pro", system-ui, sans-serif`,
                    buttonFontFamily: `"Be Vietnam Pro", system-ui, sans-serif`,
                    inputFontFamily: `"Be Vietnam Pro", system-ui, sans-serif`,
                    labelFontFamily: `"Be Vietnam Pro", system-ui, sans-serif`,
                  },
                },
              },
              className: {
                container: 'auth-form-container',
                label: 'auth-form-label',
                button: 'auth-form-button',
                input: 'auth-form-input',
                anchor: (className: string) => {
                  if (className.includes('sbui-typography-link') && className.includes('sbui-auth-form-link')) {
                    return 'text-xl';
                  }
                  return className;
                },
              },
            }}
            localization={{
              variables: {
                sign_in: {
                  email_label: 'Địa chỉ email',
                  password_label: 'Mật khẩu',
                  button_label: 'Đăng nhập',
                  loading_button_label: 'Đang đăng nhập...',
                  social_provider_text: 'Đăng nhập với {{provider}}',
                  link_text: 'Đã có tài khoản? Đăng nhập',
                  email_input_placeholder: 'Nhập địa chỉ email của bạn',
                  password_input_placeholder: 'Nhập mật khẩu của bạn',
                },
                sign_up: {
                  email_label: 'Địa chỉ email',
                  password_label: 'Mật khẩu',
                  button_label: 'Đăng ký',
                  loading_button_label: 'Đang đăng ký...',
                  social_provider_text: 'Đăng ký với {{provider}}',
                  link_text: 'Chưa có tài khoản? Đăng ký',
                  email_input_placeholder: 'Nhập địa chỉ email của bạn',
                  password_input_placeholder: 'Tạo mật khẩu',
                  confirmation_text: 'Kiểm tra email của bạn để xác nhận đăng ký',
                },
                forgotten_password: {
                  email_label: 'Địa chỉ email',
                  password_label: 'Mật khẩu',
                  button_label: 'Gửi hướng dẫn đặt lại mật khẩu',
                  loading_button_label: 'Đang gửi hướng dẫn...',
                  link_text: 'Quên mật khẩu?',
                  confirmation_text: 'Kiểm tra email của bạn để đặt lại mật khẩu',
                },
                magic_link: {
                  email_input_label: 'Địa chỉ email',
                  email_input_placeholder: 'Nhập địa chỉ email của bạn',
                  button_label: 'Gửi liên kết đăng nhập',
                  loading_button_label: 'Đang gửi liên kết...',
                  link_text: 'Gửi liên kết đăng nhập qua email',
                  confirmation_text: 'Kiểm tra email của bạn để đăng nhập',
                },
                update_password: {
                  password_label: 'Mật khẩu mới',
                  password_input_placeholder: 'Nhập mật khẩu mới',
                  button_label: 'Cập nhật mật khẩu',
                  loading_button_label: 'Đang cập nhật mật khẩu...',
                  confirmation_text: 'Mật khẩu của bạn đã được cập nhật',
                },
                verify_otp: {
                  email_input_label: 'Địa chỉ email',
                  email_input_placeholder: 'Nhập địa chỉ email của bạn',
                  phone_input_label: 'Số điện thoại',
                  phone_input_placeholder: 'Nhập số điện thoại của bạn',
                  token_input_label: 'Mã xác thực',
                  token_input_placeholder: 'Nhập mã xác thực',
                  button_label: 'Xác thực',
                  loading_button_label: 'Đang xác thực...',
                },
              },
            }}
            providers={["google"]}
          />
        </div>
      </div>
    </div>
  );
};

export default Auth;